import {
    createAsyncThunk,
    createSelector,
    createSlice,
} from "@reduxjs/toolkit";
import { QualityApi } from "@genesys/cloud-frontend/apis/QualityApi";
import type { RootState } from "app/store";
import type { LoadStatus } from "features/common/LoadStatus";
import type { AgentScoringRule } from "@genesys/cloud-frontend/models";
import { API_CONFIGURATION } from "util/apiUtils";
import { resolveEvaluationFormNames } from "features/evaluationForms/evaluationFormNamesSlice";
import { sortRulesByStatus } from "./agentScoringRuleUtils";

/** Reject payload for create/update, surfacing the HTTP status for 409 handling. */
export type SaveRejectValue = { status?: number };

/**
 * Extracts the platform error status from a rejected API call. Matches the
 * repo convention (see qualityFormSlice/scorableSlice) of treating the thrown
 * error as a `Response` and reading `status` from its parsed JSON body.
 */
async function getErrorStatus(error: unknown): Promise<number | undefined> {
    try {
        const body = await (error as Response).json();
        return body?.status;
    } catch {
        return undefined;
    }
}

type AgentScoringRulesState = {
    /** Program the currently loaded rules belong to. */
    programId?: string;
    /** Rules keyed by rule id. */
    rules: Record<string, AgentScoringRule>;
    /** Status of the list fetch. */
    loadStatus: LoadStatus;
    /** Rule ids with an in-flight enable/disable toggle (for spinner + revert). */
    togglingRuleIds: string[];
};

const initialState: AgentScoringRulesState = {
    programId: undefined,
    rules: {},
    loadStatus: "idle",
    togglingRuleIds: [],
};

/** Builds a `ruleId -> rule` lookup map from a list of rules. */
function buildRuleIdToRuleMap(
    rules: AgentScoringRule[]
): Record<string, AgentScoringRule> {
    return rules.reduce<Record<string, AgentScoringRule>>((acc, rule) => {
        if (rule.id) {
            acc[rule.id] = rule;
        }
        return acc;
    }, {});
}

/**
 * Loads all agent scoring rules for a program and resolves the evaluation form
 * names referenced by those rules into the list's `evaluationFormNames` cache
 * (best-effort: a name lookup failure is swallowed there so the table still
 * renders). Awaited so names are ready in the cache alongside the rules.
 */
export const fetchAgentScoringRules = createAsyncThunk(
    "agentScoringRules/fetchAgentScoringRules",
    async (programId: string, { dispatch }) => {
        const qualityApi = new QualityApi(API_CONFIGURATION);
        const listing = await qualityApi.getQualityProgramAgentscoringrules({
            programId,
        });
        const rules = listing.entities ?? [];

        const contextIds = [
            ...new Set(
                rules
                    .map((rule) => rule.evaluationFormContextId)
                    .filter((id): id is string => !!id)
            ),
        ];
        if (contextIds.length > 0) {
            // Dispatch (not unwrap): a rejected best-effort lookup must not fail
            // the rules load — the shared slice keeps prior names on rejection.
            await dispatch(resolveEvaluationFormNames(contextIds));
        }

        return { programId, rules };
    }
);

export const getAgentScoringRuleById = createAsyncThunk(
    "agentScoringRules/getAgentScoringRuleById",
    async ({ programId, ruleId }: { programId: string; ruleId: string }) => {
        const qualityApi = new QualityApi(API_CONFIGURATION);
        return qualityApi.getQualityProgramAgentscoringrule({
            programId,
            ruleId,
        });
    }
);

export const createAgentScoringRule = createAsyncThunk<
    AgentScoringRule,
    { programId: string; rule: AgentScoringRule },
    { rejectValue: SaveRejectValue }
>(
    "agentScoringRules/createAgentScoringRule",
    async ({ programId, rule }, { rejectWithValue }) => {
        try {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            return await qualityApi.postQualityProgramAgentscoringrules({
                programId,
                body: rule,
            });
        } catch (error) {
            return rejectWithValue({ status: await getErrorStatus(error) });
        }
    }
);

export const updateAgentScoringRule = createAsyncThunk<
    AgentScoringRule,
    { programId: string; ruleId: string; rule: AgentScoringRule },
    { rejectValue: SaveRejectValue }
>(
    "agentScoringRules/updateAgentScoringRule",
    async ({ programId, ruleId, rule }, { rejectWithValue }) => {
        try {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            return await qualityApi.putQualityProgramAgentscoringrule({
                programId,
                ruleId,
                body: rule,
            });
        } catch (error) {
            return rejectWithValue({ status: await getErrorStatus(error) });
        }
    }
);

export const deleteAgentScoringRule = createAsyncThunk(
    "agentScoringRules/deleteAgentScoringRule",
    async ({ programId, ruleId }: { programId: string; ruleId: string }) => {
        const qualityApi = new QualityApi(API_CONFIGURATION);
        await qualityApi.deleteQualityProgramAgentscoringrule({
            programId,
            ruleId,
        });
        return ruleId;
    }
);

/**
 * Optimistically sets a rule's `enabled` flag to the given value and persists it.
 * The pending case updates the UI immediately; on failure the change is reverted.
 *
 * `getState()` below is the creator's first statement (no `await`), so it runs
 * in the same turn as the `pending` update — no concurrent thunk can interleave
 * between them. The full-object PUT sends the store's current rule snapshot plus
 * the explicit `enabled` (intentional last-write-wins); revisit if we ever allow
 * editing a rule while its enabled update is in flight.
 */
export const setRuleEnabled = createAsyncThunk(
    "agentScoringRules/setRuleEnabled",
    async (
        {
            programId,
            ruleId,
            enabled,
        }: { programId: string; ruleId: string; enabled: boolean },
        { getState }
    ) => {
        const rule = (getState() as RootState).agentScoringRules.rules[ruleId];
        if (!rule) {
            throw new Error(`Agent scoring rule ${ruleId} not found in state`);
        }
        const qualityApi = new QualityApi(API_CONFIGURATION);
        return qualityApi.putQualityProgramAgentscoringrule({
            programId,
            ruleId,
            body: { ...rule, enabled },
        });
    }
);

const agentScoringRulesSlice = createSlice({
    name: "agentScoringRules",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchAgentScoringRules.pending, (state, { meta }) => {
                state.loadStatus = "loading";
                // Switching program invalidates previously loaded rules.
                if (state.programId !== meta.arg) {
                    state.programId = meta.arg;
                    state.rules = {};
                }
            })
            .addCase(fetchAgentScoringRules.fulfilled, (state, { payload }) => {
                // Ignore a response whose program is no longer the current one
                // (a later fetch for a different program has superseded it).
                if (payload.programId !== state.programId) {
                    return;
                }
                state.loadStatus = "succeeded";
                state.rules = buildRuleIdToRuleMap(payload.rules);
                // `togglingRuleIds` is intentionally left as-is: an in-flight
                // setRuleEnabled clears its own id (and re-applies the rule) on
                // settle, so a fetch/toggle overlap self-heals. Clearing it here
                // wouldn't fix the race — a late rejected revert (enabled =
                // !enabled) could then write a stale value over fresh data.
            })
            .addCase(fetchAgentScoringRules.rejected, (state, { meta }) => {
                if (meta.arg !== state.programId) {
                    return;
                }
                // Keep any previously loaded rules so a failed *refetch* leaves
                // the last-known data visible. The list route toasts on failure
                // and shows a load-failed empty state when there are no rules.
                state.loadStatus = "failed";
            })
            .addCase(
                getAgentScoringRuleById.fulfilled,
                (state, { payload }) => {
                    if (payload.id) {
                        state.rules[payload.id] = payload;
                    }
                }
            )
            .addCase(createAgentScoringRule.fulfilled, (state, { payload }) => {
                if (payload.id) {
                    state.rules[payload.id] = payload;
                }
            })
            .addCase(updateAgentScoringRule.fulfilled, (state, { payload }) => {
                if (payload.id) {
                    state.rules[payload.id] = payload;
                }
            })
            .addCase(deleteAgentScoringRule.fulfilled, (state, { payload }) => {
                delete state.rules[payload];
            })
            .addCase(setRuleEnabled.pending, (state, { meta }) => {
                const { ruleId, enabled } = meta.arg;
                const rule = state.rules[ruleId];
                if (rule) {
                    rule.enabled = enabled;
                }
                if (!state.togglingRuleIds.includes(ruleId)) {
                    state.togglingRuleIds.push(ruleId);
                }
            })
            .addCase(setRuleEnabled.fulfilled, (state, { payload, meta }) => {
                if (payload.id) {
                    state.rules[payload.id] = payload;
                }
                state.togglingRuleIds = state.togglingRuleIds.filter(
                    (id) => id !== meta.arg.ruleId
                );
            })
            .addCase(setRuleEnabled.rejected, (state, { meta }) => {
                const { ruleId, enabled } = meta.arg;
                const rule = state.rules[ruleId];
                if (rule) {
                    // Revert the optimistic flip.
                    rule.enabled = !enabled;
                }
                state.togglingRuleIds = state.togglingRuleIds.filter(
                    (id) => id !== ruleId
                );
            });
    },
});

// ---- Selectors ----

const selectAgentScoringRulesMap = (state: RootState) =>
    state.agentScoringRules.rules;

export const selectAgentScoringRulesLoadStatus = (state: RootState) =>
    state.agentScoringRules.loadStatus;

export const selectIsAgentScoringRulesLoading = (state: RootState) =>
    state.agentScoringRules.loadStatus === "loading";

/**
 * Rules as an array, enabled ones first.
 * Keyed on the rules map only, so unrelated slice changes (loadStatus,
 * togglingRuleIds) don't bust memoization or force a re-sort.
 */
export const selectAgentScoringRules = createSelector(
    selectAgentScoringRulesMap,
    (rules) => sortRulesByStatus(Object.values(rules))
);

export const selectAgentScoringRulesCount = (state: RootState) =>
    Object.keys(state.agentScoringRules.rules).length;

export const selectAgentScoringRule = (state: RootState, ruleId: string) =>
    state.agentScoringRules.rules[ruleId];

/**
 * `togglingRuleIds` as a Set, memoized on the array reference (rebuilt only when
 * a toggle starts/ends), so per-row lookups are O(1). Prefer this in lists;
 * use `selectIsRuleToggling` for a single rule id.
 */
export const selectTogglingRuleIdSet = createSelector(
    (state: RootState) => state.agentScoringRules.togglingRuleIds,
    (ids) => new Set(ids)
);

export const selectIsRuleToggling = (state: RootState, ruleId: string) =>
    selectTogglingRuleIdSet(state).has(ruleId);

export default agentScoringRulesSlice.reducer;
